/**
 * =============================================================================
 * KpiSparkline
 *
 * Mini-gráfico compacto para KPIs da aba Assinaturas do painel WL.
 * Dois modos:
 *   - "line":      série contínua (assinantes ativos, ticket médio, perdidos).
 *   - "waterfall": barras positivas (novos) sobre 0 e negativas (cancelados)
 *                  abaixo de 0, com tooltip mostrando "Novos / Cancelados / Saldo".
 *
 * Reaproveita a paleta já validada:
 *   - Roxo  (#8b7cf6) — ganhos / positivos
 *   - Âmbar (#f5a524) — cancelamentos / negativos
 *
 * @version 1.5.0
 * RELEASE: 2026-09-18 v1.5.0 - Rótulo de mês deixa de depender do fuso do
 * navegador: "AAAA-MM" passa a ser formatado a partir do próprio texto,
 * evitando exibir o mês anterior (ex.: setembro aparecendo como "ago de 26").
 * RELEASE: 2026-09-16 v1.4.0 - Adiciona modo de barras comparativas e opções
 * de pontos permanentes, eixo vertical e 12 rótulos para o gráfico do CRM.
 * RELEASE: 2026-09-16 v1.3.0 - Adiciona prop `className` opcional ao
 * container, permitindo esticar o gráfico para preencher cards maiores.
 * RELEASE: 2026-09-16 v1.2.0 - Permite exibir opcionalmente no tooltip a
 * diferença não negativa entre a série comparativa e a série principal.
 * RELEASE: 2026-09-16 v1.1.0 - Adiciona série comparativa opcional no modo
 * linha para reutilização no CRM, sem alterar os consumidores atuais.
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { cn } from '@/lib/utils';
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';


// Paleta consistente com o resto do dashboard financeiro WL.
const COR_POSITIVO = '#8b7cf6';
const COR_NEGATIVO = '#f5a524';

/** Ponto para o modo linha. */
export interface SparkLinePoint {
  month: string;
  value: number;
  /** Valor comparativo opcional, como a meta mensal. */
  comparisonValue?: number;
}

/** Ponto para o modo waterfall. */
export interface SparkWaterfallPoint {
  month: string;
  /** Positivo (>=0) — novos. */
  positive: number;
  /** Negativo (<=0) — cancelados (armazenar como negativo). */
  negative: number;
}

interface TrendProps {
  mode: 'line' | 'bar';
  data: SparkLinePoint[];
  /** Cor da linha; default roxo. */
  color?: string;
  /** Formatador de valor para o tooltip. */
  formatValue?: (v: number) => string;
  /** Rótulo da série principal no tooltip. */
  valueLabel?: string;
  /** Rótulo da série comparativa opcional. */
  comparisonLabel?: string;
  /** Cor da série comparativa. */
  comparisonColor?: string;
  /** Rótulo opcional da diferença restante até a série comparativa. */
  shortfallLabel?: string;
  /** Classe CSS extra para o container do gráfico (ex.: altura flexível). */
  className?: string;
  /** Exibe marcadores permanentes nas séries do modo linha. */
  showDots?: boolean;
  /** Exibe o eixo vertical com a escala dos valores. */
  showYAxis?: boolean;
  /** Formata os valores exibidos no eixo vertical. */
  formatYAxis?: (v: number) => string;
  /** Força a exibição dos 12 meses com rótulo numérico compacto. */
  showAllMonths?: boolean;
}

interface WaterfallProps {
  mode: 'waterfall';
  data: SparkWaterfallPoint[];
  /** Classe CSS extra para o container do gráfico. */
  className?: string;
}

type Props = TrendProps | WaterfallProps;

// Nomes curtos de mês em pt-BR (índice 1..12), usados sem depender de fuso.
const MESES_CURTOS_PT_BR = [
  '',
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

/**
 * Extrai ano e mês de "YYYY-MM" ou "YYYY-MM-DD" lendo o próprio texto.
 * v1.5.0: evita `new Date`, que interpretava a data como UTC e, ao formatar no
 * fuso do navegador (São Paulo, UTC-3), voltava um dia — e um mês inteiro
 * quando a data era o dia 1º.
 */
function parseAnoMes(month: string): { ano: number; mes: number } | null {
  const match = /^(\d{4})-(\d{2})/.exec(month);
  if (!match) return null;
  const mes = Number(match[2]);
  if (mes < 1 || mes > 12) return null;
  return { ano: Number(match[1]), mes };
}

// Formata "YYYY-MM" ou "YYYY-MM-DD" para "mmm de aa" pt-BR.
function fmtMonthLabel(month: string): string {
  const parsed = parseAnoMes(month);
  if (!parsed) return month;
  const ano = String(parsed.ano).slice(-2);
  return `${MESES_CURTOS_PT_BR[parsed.mes]} de ${ano}`;
}

/** Formata o mês como número de dois dígitos para acomodar uma série anual. */
function fmtCompactMonthLabel(month: string): string {
  const parsed = parseAnoMes(month);
  if (!parsed) return month;
  return String(parsed.mes).padStart(2, '0');
}

/**
 * Sparkline compacto (≈120px de altura por padrão) para uso dentro de cards KPI.
 * Pode ser esticado via `className` quando o card precisa ocupar mais espaço.
 */
export function KpiSparkline(props: Props) {
  if (props.mode === 'line' || props.mode === 'bar') {
    const {
      mode,
      data,
      color = COR_POSITIVO,
      formatValue,
      valueLabel = 'Valor',
      comparisonLabel,
      comparisonColor = 'hsl(var(--muted-foreground))',
      shortfallLabel,
      className,
      showDots = false,
      showYAxis = false,
      formatYAxis,
      showAllMonths = false,
    } = props;
    const hasComparison = Boolean(comparisonLabel && data.some((point) => point.comparisonValue !== undefined));
    const eixoHorizontal = (
      <XAxis
        dataKey="month"
        tickFormatter={showAllMonths ? fmtCompactMonthLabel : fmtMonthLabel}
        tick={{ fontSize: 10 }}
        tickLine={false}
        axisLine={false}
        interval={showAllMonths ? 0 : 'preserveStartEnd'}
        minTickGap={showAllMonths ? 0 : 16}
      />
    );
    const eixoVertical = showYAxis ? (
      <YAxis
        tick={{ fontSize: 10 }}
        tickLine={false}
        axisLine={false}
        width={44}
        tickFormatter={(value) => formatYAxis ? formatYAxis(Number(value)) : String(value)}
      />
    ) : (
      <YAxis hide />
    );
    const tooltip = (
      <Tooltip
        cursor={mode === 'bar' ? { fill: 'hsl(var(--muted) / 0.4)' } : { stroke: 'hsl(var(--border))' }}
        content={({ active, payload, label }) => {
          if (!active || !payload || payload.length === 0) return null;
          const point = payload[0]?.payload as SparkLinePoint | undefined;
          if (!point) return null;

          const format = (value: number) => formatValue ? formatValue(value) : String(value);
          const shortfall = Math.max((point.comparisonValue ?? 0) - point.value, 0);

          return (
            <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
              <p className="mb-1.5 font-medium">{fmtMonthLabel(String(label))}</p>
              <p>{valueLabel}: <strong>{format(point.value)}</strong></p>
              {hasComparison && (
                <p>{comparisonLabel}: <strong>{format(point.comparisonValue ?? 0)}</strong></p>
              )}
              {shortfallLabel && hasComparison && (
                <p>{shortfallLabel}: <strong>{format(shortfall)}</strong></p>
              )}
            </div>
          );
        }}
      />
    );

    return (
      <div className={cn('h-[120px] w-full', className)}>
        <ResponsiveContainer width="100%" height="100%">
          {mode === 'line' ? (
            <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              {eixoHorizontal}
              {eixoVertical}
              {tooltip}
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={showDots ? { r: 3, fill: 'hsl(var(--card))', stroke: color, strokeWidth: 2 } : false}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
              {hasComparison && (
                <Line
                  type="monotone"
                  dataKey="comparisonValue"
                  stroke={comparisonColor}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={showDots ? { r: 3, fill: 'hsl(var(--card))', stroke: comparisonColor, strokeWidth: 2 } : false}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              {eixoHorizontal}
              {eixoVertical}
              {tooltip}
              <Bar dataKey="value" fill={color} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              {hasComparison && (
                <Bar dataKey="comparisonValue" fill={comparisonColor} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              )}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    );
  }

  // Modo waterfall — positivas em cima do zero, negativas abaixo.
  const { data, className } = props;
  return (
    <div className={cn('h-[120px] w-full', className)}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="month"
            tickFormatter={fmtMonthLabel}
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis hide />
          <ReferenceLine y={0} stroke="hsl(var(--border))" />
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              fontSize: 11,
            }}
            labelFormatter={(l) => fmtMonthLabel(String(l))}
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null;
              const p = payload[0].payload as SparkWaterfallPoint;
              const saldo = (p.positive || 0) + (p.negative || 0);
              return (
                <div
                  style={{
                    background: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                    padding: '6px 8px',
                    fontSize: 11,
                  }}
                >
                  <div className="font-medium mb-1">{fmtMonthLabel(String(label))}</div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 rounded-sm"
                      style={{ background: COR_POSITIVO }}
                    />
                    <span>Novos: {p.positive}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 rounded-sm"
                      style={{ background: COR_NEGATIVO }}
                    />
                    <span>Cancelados: {Math.abs(p.negative)}</span>
                  </div>
                  <div className="mt-1 pt-1 border-t border-border/50 font-medium">
                    Saldo: {saldo >= 0 ? '+' : ''}
                    {saldo}
                  </div>
                </div>
              );
            }}
          />
          <Bar
            dataKey="positive"
            fill={COR_POSITIVO}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
          <Bar
            dataKey="negative"
            fill={COR_NEGATIVO}
            radius={[0, 0, 2, 2]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
